// Lógica de precios de la calculadora USFQ (Motivy)
// Todo el estado del modo cliente viaja en la URL, sin backend.

export const USUARIOS_RRHH = 1200;
export const TRAMO_MAX = 3900; // estudiantes por tramo

export const DEFAULTS = {
  precioRRHH: 3.2,
  precioTramo1: 1.4,
  precioTramo2: 0.6,
  descuentoTele: 25,
  descuentoNutri: 15,
  descuentoModulo: 10,
  // Supuestos de ahorro / ROI de telepsicología (mensual)
  roiMargen: 40, // % de ahorro que aporta este servicio sobre la inversión
  roiCitasAno: 4.8, // citas promedio al año por persona
  roiCostoCita: 55, // costo de mercado por cita ($)
  // Supuestos de ahorro / ROI de telenutrición (mensual)
  roiMargenNutri: 27,
  roiCitasAnoNutri: 2,
  roiCostoCitaNutri: 55,
  // Aumento del retorno por escala: cuánto sube el % de retorno al 100% de estudiantes vs 50%
  roiEscala100: 30,
  // Estadística de impacto: % de estudiantes que no usa estos servicios por el costo
  statNoUsa: 73,
};

// Opciones visibles para el cliente
export const OPCIONES_ESTUDIANTES = [
  { value: 0, label: "Solo RRHH", sub: "0 estudiantes" },
  { value: 3900, label: "50%", sub: "3,900 estudiantes" },
  { value: 7800, label: "100%", sub: "7,800 estudiantes" },
];

// Mapea claves cortas de query params <-> variables
const PARAM_KEYS = {
  precioRRHH: "r",
  precioTramo1: "t1",
  precioTramo2: "t2",
  descuentoTele: "dt",
  descuentoNutri: "dn",
  descuentoModulo: "dm",
  roiMargen: "rm",
  roiCitasAno: "rc",
  roiCostoCita: "rp",
  roiMargenNutri: "rmn",
  roiCitasAnoNutri: "rcn",
  roiCostoCitaNutri: "rpn",
  roiEscala100: "re",
  statNoUsa: "sn",
};

// Lee los valores base desde los query params. Si no hay ninguno, usa DEFAULTS.
export function parseConfigFromSearch(search) {
  const params = new URLSearchParams(search);
  const config = { ...DEFAULTS };
  let tieneParams = false;
  for (const [key, shortKey] of Object.entries(PARAM_KEYS)) {
    const raw = params.get(shortKey);
    if (raw !== null && raw !== "" && !Number.isNaN(Number(raw))) {
      config[key] = Number(raw);
      tieneParams = true;
    }
  }
  return { config, tieneParams };
}

// Construye la URL para el cliente con los valores base codificados
export function buildClientUrl(config, origin) {
  const params = new URLSearchParams();
  for (const [key, shortKey] of Object.entries(PARAM_KEYS)) {
    params.set(shortKey, String(config[key]));
  }
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/?${params.toString()}`;
}

// Los 4 pilares del plan. Los que tienen servicios ilimitados aplican mayor descuento al retirarse.
export const MODULOS = [
  { key: "nutricion", label: "Nutrición", detalle: "IA + Telenutrición ilimitada", ilimitado: true, descuentoKey: "descuentoNutri" },
  { key: "fisica", label: "Salud Física", detalle: "Actividad y bienestar físico", ilimitado: false, descuentoKey: "descuentoModulo" },
  { key: "descanso", label: "Descanso", detalle: "Sueño y recuperación", ilimitado: false, descuentoKey: "descuentoModulo" },
  { key: "mental", label: "Salud Mental", detalle: "Telepsicología ilimitada", ilimitado: true, descuentoKey: "descuentoTele" },
];
export const MAX_MODULOS_QUITADOS = 2;

// Selección por defecto del cliente
export const SELECCION_DEFAULT = {
  estudiantes: 3900,
  modulosQuitados: [],
};

// Cálculo principal
export function calcular(config, seleccion) {
  const { precioRRHH, precioTramo1, precioTramo2 } = config;
  let { estudiantes, modulosQuitados } = seleccion;
  if (!Array.isArray(modulosQuitados)) modulosQuitados = [];

  // Regla: si solo RRHH (0 estudiantes) no aplican descuentos
  if (estudiantes === 0) {
    modulosQuitados = [];
  }

  const descuentoTotal = modulosQuitados.reduce((suma, key) => {
    const mod = MODULOS.find((m) => m.key === key);
    return suma + (mod ? Number(config[mod.descuentoKey]) || 0 : 0);
  }, 0);
  let factor = 1 - descuentoTotal / 100;
  if (factor < 0) factor = 0;

  // Compatibilidad con el cálculo de ahorro (servicios ilimitados activos)
  const teleRetirada = modulosQuitados.includes("mental");
  const nutriRetirada = modulosQuitados.includes("nutricion");

  const precioTramo1Final = precioTramo1 * factor;
  const precioTramo2Final = precioTramo2 * factor;

  const estudiantesEnTramo1 = Math.min(estudiantes, TRAMO_MAX);
  const estudiantesEnTramo2 = Math.max(0, estudiantes - TRAMO_MAX);

  const ingresoRRHH = USUARIOS_RRHH * precioRRHH;
  const ingresoEstudiantes =
    estudiantesEnTramo1 * precioTramo1Final + estudiantesEnTramo2 * precioTramo2Final;

  const totalMensual = ingresoRRHH + ingresoEstudiantes;
  const usuariosTotales = USUARIOS_RRHH + estudiantes;
  const precioPromedioPorUsuario = usuariosTotales > 0 ? totalMensual / usuariosTotales : 0;

  const montoMostradoRRHH = USUARIOS_RRHH * precioPromedioPorUsuario;
  const montoMostradoEstudiantes = estudiantes * precioPromedioPorUsuario;

  return {
    factor,
    precioRRHH,
    precioTramo1Final,
    precioTramo2Final,
    estudiantesEnTramo1,
    estudiantesEnTramo2,
    estudiantes,
    totalMensual,
    usuariosTotales,
    precioPromedioPorUsuario,
    montoMostradoRRHH,
    montoMostradoEstudiantes,
    teleRetirada,
    nutriRetirada,
    modulosQuitados,
  };
}

const fmtMoneda = new Intl.NumberFormat("es-EC", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtMonedaLarga = new Intl.NumberFormat("es-EC", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtNumero = new Intl.NumberFormat("es-EC");

const fmtMoneda4 = new Intl.NumberFormat("es-EC", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export const dinero = (n) => fmtMoneda.format(n || 0);
export const dineroGrande = (n) => fmtMonedaLarga.format(n || 0);
export const dineroPreciso = (n) => fmtMoneda4.format(n || 0);
export const numero = (n) => fmtNumero.format(n || 0);

const fmtNumero1 = new Intl.NumberFormat("es-EC", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
export const numeroDec = (n) => fmtNumero1.format(n || 0);

// Ahorro mensual de un servicio de telesalud (participación del ahorro total combinado).
// La adopción de usuarios se despeja para que las cifras cuadren.
function ahorroServicio(nombre, margen, share, ahorroTotal, usuarios, citasAno, costoCita) {
  const ahorro = ahorroTotal * share;
  const citasMesPorPersona = citasAno / 12;
  const costoPersonaMes = citasMesPorPersona * costoCita;
  const personas = costoPersonaMes > 0 ? ahorro / costoPersonaMes : 0;
  const adopcion = usuarios > 0 ? (personas / usuarios) * 100 : 0;
  return { nombre, margen, citasAno, costoCita, ahorro, personas, adopcion, citasMesPorPersona };
}

// Cálculo de ahorro / ROI (mensual). El retorno combinado es la suma de los márgenes
// de los servicios activos y se reparte entre ellos según su margen (telepsicología mayor).
export function calcularAhorro(config, resultado) {
  const usuarios = resultado.usuariosTotales;
  const inversionMensual = resultado.totalMensual;
  const teleActiva = !resultado.teleRetirada;
  const nutriActiva = !resultado.nutriRetirada;

  const margenTele = teleActiva ? Number(config.roiMargen) || 0 : 0;
  const margenNutri = nutriActiva ? Number(config.roiMargenNutri) || 0 : 0;
  const margenBase = margenTele + margenNutri;

  // Escala por número de estudiantes (economías de escala): anclada en el 50% (=3900).
  // Al 100% (7800) el retorno sube roiEscala100 %; con menos estudiantes baja.
  const estudiantes = resultado.estudiantes;
  const escalaPct = Number(config.roiEscala100) || 0;
  let escala = 1 + (escalaPct / 100) * ((estudiantes - 3900) / 3900);
  if (escala < 0) escala = 0;

  const margenEfectivo = margenBase * escala;
  const hayServicio = margenBase > 0;

  const ahorroMensual = hayServicio ? inversionMensual * (1 + margenEfectivo / 100) : 0;
  const beneficioNetoMensual = hayServicio ? ahorroMensual - inversionMensual : 0;

  const tele = teleActiva
    ? ahorroServicio(
        "Telepsicología ilimitada",
        margenTele,
        margenBase > 0 ? margenTele / margenBase : 0,
        ahorroMensual,
        usuarios,
        Number(config.roiCitasAno) || 0,
        Number(config.roiCostoCita) || 0
      )
    : null;

  const nutri = nutriActiva
    ? ahorroServicio(
        "Telenutrición ilimitada",
        margenNutri,
        margenBase > 0 ? margenNutri / margenBase : 0,
        ahorroMensual,
        usuarios,
        Number(config.roiCitasAnoNutri) || 0,
        Number(config.roiCostoCitaNutri) || 0
      )
    : null;

  const servicios = [tele, nutri].filter(Boolean);

  // Recuperación con una sola cita (usa el costo de un servicio activo)
  const costoRefCita = tele?.costoCita || nutri?.costoCita || 0;
  const precioPromedioPorUsuario = resultado.precioPromedioPorUsuario;
  const costoLicenciaAnualUsuario = precioPromedioPorUsuario * 12;
  const vecesRecuperaUnaCita =
    costoLicenciaAnualUsuario > 0 ? costoRefCita / costoLicenciaAnualUsuario : 0;

  // Punto de equilibrio: cuántas citas al mes se necesitan para cubrir la inversión mensual,
  // y qué % de usuarios bastaría que tome 1 cita al mes para que la iniciativa se pague sola.
  const citasParaPagar = costoRefCita > 0 ? inversionMensual / costoRefCita : 0;
  const tasaUsuariosPagar =
    usuarios > 0 && costoRefCita > 0 ? (citasParaPagar / usuarios) * 100 : 0;

  return {
    usuarios,
    inversionMensual,
    teleActiva,
    nutriActiva,
    tele,
    nutri,
    servicios,
    hayServicio,
    ahorroMensual,
    beneficioNetoMensual,
    margenEfectivo,
    precioPromedioPorUsuario,
    costoLicenciaAnualUsuario,
    costoRefCita,
    vecesRecuperaUnaCita,
    citasParaPagar,
    tasaUsuariosPagar,
  };
}

